/**
 * ProviderStatusBadge — coloured pill for provider status values.
 *
 * Colour coding follows the plan spec:
 *   PENDING  → amber
 *   ACTIVE   → green
 *   INACTIVE → grey
 *
 * Reused on the Providers List page (Feature D) and Provider Profile header
 * (Feature E).
 */

import { cn } from "@/lib/utils";
import type { ProviderStatus } from "@/types/providers";

interface ProviderStatusBadgeProps {
  status: ProviderStatus;
  className?: string;
}

const STATUS_STYLES: Record<
  ProviderStatus,
  { dot: string; bg: string; text: string; label: string }
> = {
  pending: {
    dot: "bg-amber-500",
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-700",
    label: "Pending",
  },
  active: {
    dot: "bg-green-500",
    bg: "bg-green-50 border-green-200",
    text: "text-green-700",
    label: "Active",
  },
  inactive: {
    dot: "bg-gray-400",
    bg: "bg-gray-50 border-gray-200",
    text: "text-gray-600",
    label: "Inactive",
  },
};

/** Pill badge showing provider status with a coloured dot. */
export function ProviderStatusBadge({ status, className }: ProviderStatusBadgeProps) {
  const styles = STATUS_STYLES[status] ?? STATUS_STYLES.inactive;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        styles.bg,
        styles.text,
        className
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", styles.dot)} />
      {styles.label}
    </span>
  );
}
